import {
  Box,
  ButtonBase,
  Icon,
  styled,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import useSettings from "app/hooks/useSettings";
import React, { Fragment } from "react";
import { NavLink } from "react-router-dom";
import { Paragraph, Span } from "../Typography";
import MatxVerticalNavExpansionPanel from "./MatxVerticalNavExpansionPanel";
import { getNavigationAsPerRole } from "app/navigations";
import { useCompanyConfig } from "app/hooks/useCompanyConfig";
import { NAV_ICONS } from "./navIcons";

const ListLabel = styled(Paragraph)(({ theme, mode }) => ({
  fontSize: "11px",
  fontWeight: 600,
  marginTop: "20px",
  marginLeft: "15px",
  marginBottom: "8px",
  letterSpacing: "0.5px",
  textTransform: "uppercase",
  display: mode === "compact" && "none",
  color: "#8a839c",
  fontFamily: "'DM Sans', sans-serif",
}));

const ExtAndIntCommon = {
  display: "flex",
  overflow: "hidden",
  borderRadius: "10px",
  height: 42,
  whiteSpace: "pre",
  marginBottom: "3px",
  textDecoration: "none",
  justifyContent: "space-between",
  transition: "all 150ms ease-in",
  padding: "10px 11px",
  "&:hover": { background: "#f8f5fd" },
  "&.compactNavItem": {
    overflow: "hidden",
    justifyContent: "center !important",
  },
  "& .icon": {
    fontSize: "18px",
    verticalAlign: "middle",
    color: "inherit",
  },
};
const ExternalLink = styled("a")(({ theme }) => ({
  ...ExtAndIntCommon,
  color: "#544d68",
}));

const InternalLink = styled(Box)(({ theme }) => ({
  "& a": {
    ...ExtAndIntCommon,
    color: "#544d68",
    fontWeight: 500,
  },
  "& .navItemActive": {
    background: "linear-gradient(135deg, rgba(224,89,240,0.12), rgba(152,149,244,0.12))",
    color: "#a411c1",
    fontWeight: 600,
    "& .icon": {
      color: "#a411c1",
    },
  },
}));

const StyledText = styled(Span)(({ mode }) => ({
  fontSize: "14px",
  paddingLeft: "0",
  display: mode === "compact" && "none",
  fontFamily: "'DM Sans', sans-serif",
  fontWeight: "inherit",
}));

const BulletIcon = styled("div")(() => ({
  display: "none",
}));

const BadgeValue = styled("div")(() => ({
  padding: "1px 8px",
  overflow: "hidden",
  borderRadius: "999px",
  fontSize: "11px",
  fontWeight: 600,
  background: "#e059f0",
  color: "#ffffff",
  minWidth: "20px",
  textAlign: "center",
}));

const MatxVerticalNav = ({ role }) => {
  const theme = useTheme();
  const { config, loading } = useCompanyConfig();
  const { settings, updateSettings } = useSettings();
  const isMdScreen = useMediaQuery(theme.breakpoints.down("md"));

  const updateSidebarMode = (sidebarSettings) => {
    updateSettings({
      layout1Settings: { leftSidebar: { ...sidebarSettings } },
    });
  };

  const handleSidebarToggle = () => {
    let { layout1Settings } = settings;
    let mode;
    if (isMdScreen) {
      mode = layout1Settings.leftSidebar.mode === "close" ? "mobile" : "close";
    }
    updateSidebarMode({ mode });
  };

  const { mode } = settings.layout1Settings.leftSidebar;

  const renderLevels = (data) => {
    return data.map((item, index) => {
      if (item.type === "label")
        return (
          <ListLabel key={index} mode={mode} className="sidenavHoverShow">
            {item.label}
          </ListLabel>
        );

      if (item.children) {
        return (
          <MatxVerticalNavExpansionPanel mode={mode} item={item} key={index}>
            {renderLevels(item.children)}
          </MatxVerticalNavExpansionPanel>
        );
      } else if (item.type === "extLink") {
        return (
          <ExternalLink
            key={index}
            href={item.path}
            className={`${mode === "compact" && "compactNavItem"}`}
            rel="noopener noreferrer"
            target="_blank"
          >
            <ButtonBase key={item.name} name="child" sx={{ width: "100%", display: "flex", gap: "11px", alignItems: "center" }}>
              {(() => {
                if (item.icon) {
                  return NAV_ICONS[item.icon] ? (
                    <Box sx={{ width: 17, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>{NAV_ICONS[item.icon]}</Box>
                  ) : (
                    <Icon className="icon">{item.icon}</Icon>
                  );
                } else {
                  return (
                    <span className="item-icon icon-text">{item.iconText}</span>
                  );
                }
              })()}
              <StyledText mode={mode} className="sidenavHoverShow">
                {item.name}
              </StyledText>
              <Box mx="auto"></Box>
              {item.badge && <BadgeValue>{item.badge.value}</BadgeValue>}
            </ButtonBase>
          </ExternalLink>
        );
      } else {
        return (
          <InternalLink key={index}>
            <NavLink
              to={item.path}
              className={({ isActive }) =>
                isActive
                  ? `navItemActive ${mode === "compact" && "compactNavItem"}`
                  : `${mode === "compact" && "compactNavItem"}`
              }
              onClick={handleSidebarToggle}
            >
              <ButtonBase key={item.name} name="child" sx={{ width: "100%", display: "flex", gap: "11px", alignItems: "center" }}>
                {item?.icon ? (
                  NAV_ICONS[item.icon] ? (
                    <Box sx={{ width: 17, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>{NAV_ICONS[item.icon]}</Box>
                  ) : (
                    <Icon className="icon" sx={{ width: 36 }}>
                      {item.icon}
                    </Icon>
                  )
                ) : (
                  mode === "compact" ? (
                    <Box
                      className="nav-bullet-text"
                      sx={{ fontSize: "11px", fontWeight: 600, color: "inherit", width: 17, textAlign: "center", flexShrink: 0 }}
                    >
                      {item.iconText?.charAt(0)}
                    </Box>
                  ) : null
                )}
                <StyledText mode={mode} className="sidenavHoverShow">
                  {item.name}
                </StyledText>

                <Box mx="auto" />

                {item.badge && (
                  <BadgeValue className="sidenavHoverShow">
                    {item.badge.value}
                  </BadgeValue>
                )}
              </ButtonBase>
            </NavLink>
          </InternalLink>
        );
      }
    });
  };

  return (
    <div className="navigation">
      {renderLevels(getNavigationAsPerRole(role,config))}
    </div>
  );
};

export default React.memo(MatxVerticalNav);
